// This file contains HATEOAS-compatible utility functions for the practice test

import { getLink, getLinkHref, isQuestionAnswered } from '../../services/hateoasUtils';

/**
 * Updates the response with full question and answer details for storage
 * @param {Object} response - The API response object from the exam question
 * @param {Object} question - The question object with full details
 * @param {Array} selectedAnswers - Array of selected answer objects
 * @returns {Object} Enhanced response object with question and answer text
 */
function enhanceResponseWithDetails(response, question, selectedAnswers) {
  // Create a deep copy of the response to avoid modifying the original
  const enhancedResponse = {
    ...response,
    questionText: question?.body || question?.question || question?.text,
    answerText: selectedAnswers?.map(answer => answer?.body || answer?.text || answer?.value).join(', ')
  };
  
  // console.log('Storing enhanced response with HATEOAS links:', enhancedResponse);
  
  return enhancedResponse;
}

/**
 * Checks for previous answers to a question in the stored responses
 * @param {string} questionId - The ID of the current question
 * @param {Object} question - The question object with potential alternate IDs
 * @param {Array} storedResponses - Array of previously stored responses
 * @returns {Object|null} The previous response if found, otherwise null
 */
function findPreviousResponse(questionId, question, storedResponses) {
  // If no stored responses, return null early
  if (!storedResponses || !Array.isArray(storedResponses)) {
    return null;
  }
  
  // Look for previous response matching this question using multiple ID strategies
  const previousResponse = storedResponses.find(r => 
    r.questionId === questionId || 
    (question.alternateIds && question.alternateIds.includes(r.questionId))
  );
  
  return previousResponse || null;
}

/**
 * Restores previous answer state based on the response from the API
 * @param {Object} previousResponse - The previous response object from the API
 * @param {Object} question - The question object with answer options
 * @param {Function} setSelectedAnswer - Function to set the selected answer state
 * @param {Function} setIsAnswerChecked - Function to set whether the answer is checked
 * @param {Function} setIsCorrect - Function to set whether the answer is correct
 * @param {Function} setIsFlagged - Function to set whether the question is flagged
 * @returns {boolean} True if previous state was restored, false otherwise
 */
function restorePreviousAnswerState(previousResponse, question, setSelectedAnswer, setIsAnswerChecked, setIsCorrect, setIsFlagged) {
  if (!previousResponse || !previousResponse.answerIds || !Array.isArray(previousResponse.answerIds) || !question.answers) {
    return false;
  }
  
  // If there are no answer IDs, it was skipped
  if (previousResponse.answerIds.length === 0) {
    // console.log('Question was previously skipped');
    if (previousResponse.isFlagged) {
      setIsFlagged(true);
    }
    return false;
  }

  // Check if there's a match in the answers
  const selectedAnswerIndices = [];
  
  previousResponse.answerIds.forEach(answerId => {
    const answerIndex = question.answers.findIndex(a => a.id === answerId);
    if (answerIndex !== -1) {
      selectedAnswerIndices.push(answerIndex);
    }
  });
  
  if (selectedAnswerIndices.length === 0) {
    console.warn(`Could not find matching answers with IDs ${previousResponse.answerIds.join(', ')} in question answers`);
    return false;
  }
  
  // Convert indices to letters (0 -> A, 1 -> B, etc.)
  const letters = selectedAnswerIndices.map(index => String.fromCharCode(65 + index));
  // console.log(`Setting selected answer(s) to ${letters.join(', ')} based on previous response`);
  
  // If only one answer, set as a string, otherwise set as an array (depending on your component implementation)
  if (letters.length === 1) {
    setSelectedAnswer(letters[0]);
  } else {
    setSelectedAnswer(letters);
  }
  
  // If the answer was previously checked, show the result
  if (previousResponse.isCorrect !== undefined) {
    // console.log(`Setting answer checked state to true with correctness: ${previousResponse.isCorrect}`);
    setIsAnswerChecked(true);
    setIsCorrect(previousResponse.isCorrect);
  }
  
  // If the question was flagged, show the flag
  if (previousResponse.isFlagged) {
    // console.log('Setting flagged state to true based on previous response');
    setIsFlagged(true);
  }
  
  return true;
}

/**
 * Prepares a response payload for sending to the API
 * @param {number} timeSpent - Time spent on the question in seconds
 * @param {boolean} isFlagged - Whether the question is flagged
 * @param {Array|string} selectedAnswer - The selected answer(s)
 * @param {Array} answerOptions - Available answer options
 * @returns {Object} Payload object ready to send to the API
 */
function prepareResponsePayload(timeSpent, isFlagged, selectedAnswer, answerOptions) {
  // Initialize empty answers array
  let answerIds = [];
  
  // console.log('prepareResponsePayload DEBUG:', {
  //   selectedAnswer,
  //   answerOptionsLength: answerOptions?.length || 0,
  //   firstAnswerStructure: answerOptions?.[0]
  // });
  
  // If there's a selected answer and we have answer options, convert it to the expected format
  if (selectedAnswer && answerOptions && answerOptions.length > 0) {
    // Handle both single letter (string) and array of letters
    const selectedLetters = Array.isArray(selectedAnswer) ? selectedAnswer : [selectedAnswer];
    
    // Convert letters to answer IDs
    answerIds = selectedLetters.map(letter => {
      const index = letter.charCodeAt(0) - 65; // Convert A->0, B->1, etc.
      
      if (index >= 0 && index < answerOptions.length) {
        const answer = answerOptions[index];
        const answerId = answer?.id;
        // console.log(`Letter ${letter} -> index ${index} -> answer:`, answer, 'id:', answerId);
        return answerId || null;
      } else {
        console.warn(`Index ${index} out of bounds for answers array of length ${answerOptions.length}`);
        return null;
      }
    }).filter(id => id !== null);
  } else if (selectedAnswer && (!answerOptions || answerOptions.length === 0)) {
    console.warn('prepareResponsePayload: Selected answer provided but no answer options available. This may indicate a data synchronization issue.');
  }
  
  const payload = {
    timeSpent: timeSpent || 0,
    isFlagged: isFlagged || false,
    Answers: answerIds
  };
  
  // console.log('prepareResponsePayload result:', payload);
  return payload;
}

// Export the utility functions
export {
  enhanceResponseWithDetails,
  findPreviousResponse,
  restorePreviousAnswerState,
  prepareResponsePayload
};
